import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, InputGroup, Button, Badge, Nav, Dropdown, Modal } from 'react-bootstrap';
import { FaPaperPlane, FaUserCircle, FaPaperclip, FaImage, FaFileAlt, FaEllipsisV, FaTrash, FaArrowLeft, FaUsers, FaBook, FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import ChatService from '../../services/chat.service';
import API from '../../services/api';
import './AdminChat.css';

const AdminChat = () => {
  const [activeTab, setActiveTab] = useState('student'); // 'student', 'batch', 'course'
  const [chatList, setChatList] = useState([]);
  const [activeChat, setActiveChat] = useState(null); // { id, name, type }
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadChatList();
    const interval = setInterval(loadChatList, 10000); // Polling list every 10s
    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    if (activeChat) {
      loadChatHistory();
      const interval = setInterval(loadChatHistory, 5000); // Polling history every 5s
      return () => clearInterval(interval);
    }
  }, [activeChat]);

  const loadChatList = async () => {
    try {
      const res = await ChatService.getAdminChatList(activeTab);
      setChatList(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadChatHistory = async () => {
    if (!activeChat) return;
    try {
      const res = await ChatService.getAdminChatHistory(activeChat.type, activeChat.id);
      setMessages(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() && !attachment) return;

    const formData = new FormData();
    formData.append('sender_type', 'admin');
    formData.append('sender_id', 0);
    formData.append('receiver_type', activeChat.type);
    formData.append('receiver_id', activeChat.id);
    if (messageInput.trim()) formData.append('message', messageInput.trim());
    if (attachment) formData.append('attachment', attachment);

    setMessageInput('');
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      await ChatService.sendMessage(formData);
      loadChatHistory();
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleClearChat = async () => {
    try {
      await ChatService.clearChat(activeChat.type, activeChat.id);
      setMessages([]);
      setShowClearConfirm(false);
      toast.success('Chat cleared successfully');
    } catch (err) {
      toast.error('Failed to clear chat');
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try {
      await ChatService.deleteMessage(msgId);
      loadChatHistory();
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderAttachment = (url) => {
    if (!url) return null;
    const API_URL = API.defaults.baseURL;
    const fullUrl = url.startsWith('http') ? url : `${API_URL.replace('/api', '')}/${url}`;
    const ext = url.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);

    if (isImage) {
      return (
        <div className="chat-attachment image">
          <a href={fullUrl} target="_blank" rel="noopener noreferrer">
            <img src={fullUrl} alt="attachment" />
          </a>
        </div>
      );
    }
    return (
      <div className="chat-attachment file">
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center text-decoration-none">
          <FaFileAlt className="me-2" /> View Attachment
        </a>
      </div>
    );
  };

  const filteredList = chatList.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="admin-chat-page bg-light min-vh-100">
      <Header />
      <div className="admin-chat-container">
        <Row className="h-100 g-0 border rounded shadow-sm overflow-hidden bg-white mx-3 mb-3">
          
          {/* LEFT SIDEBAR: Chat List */}
          {(!isMobileView || !activeChat) && (
            <Col md={4} className="chat-sidebar border-end d-flex flex-column h-100">
              <div className="chat-sidebar-header p-3 bg-light border-bottom">
                <h5 className="mb-3">Messages</h5>
                <Nav variant="pills" className="chat-nav-pills d-flex mb-3">
                  <Nav.Item className="flex-fill text-center">
                    <Nav.Link 
                      active={activeTab === 'student'} 
                      onClick={() => { setActiveTab('student'); setActiveChat(null); setSearchQuery(''); }}
                    >
                      <FaUserCircle className="me-1" /> Students
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="flex-fill text-center">
                    <Nav.Link 
                      active={activeTab === 'batch'} 
                      onClick={() => { setActiveTab('batch'); setActiveChat(null); setSearchQuery(''); }}
                    >
                      <FaUsers className="me-1" /> Batches
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="flex-fill text-center">
                    <Nav.Link 
                      active={activeTab === 'course'} 
                      onClick={() => { setActiveTab('course'); setActiveChat(null); setSearchQuery(''); }}
                    >
                      <FaBook className="me-1" /> Courses
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <InputGroup>
                  <InputGroup.Text className="bg-white border-end-0 text-muted">
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control 
                    placeholder="Search..." 
                    className="border-start-0 shadow-none bg-white"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </div>

              <div className="chat-list flex-grow-1 overflow-auto">
                {filteredList.length === 0 ? (
                  <div className="text-center text-muted p-4">No {activeTab}s found.</div>
                ) : (
                  filteredList.map(item => (
                    <div 
                      key={item.id} 
                      className={`chat-list-item d-flex align-items-center p-3 border-bottom ${activeChat?.id === item.id ? 'active' : ''}`}
                      onClick={() => setActiveChat({ id: item.id, name: item.name, type: activeTab })}
                    >
                      <div className="chat-avatar me-3 bg-primary text-white d-flex align-items-center justify-content-center rounded-circle" style={{ width: '45px', height: '45px', fontSize: '1.2rem' }}>
                        {item.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="d-flex justify-content-between align-items-baseline">
                          <h6 className="mb-0 text-truncate">{item.name}</h6>
                          {item.last_message_time && (
                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                              {formatTime(item.last_message_time)}
                            </small>
                          )}
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <small className="text-muted text-truncate" style={{ maxWidth: '80%' }}>
                            {activeTab !== 'student' ? 'Broadcast channel' : (item.last_message || 'No messages yet')}
                          </small>
                          {item.unread_count > 0 && (
                            <Badge bg="success" pill>{item.unread_count}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Col>
          )}

          {/* RIGHT SIDEBAR: Chat View */}
          {(!isMobileView || activeChat) && (
            <Col md={8} className="chat-main d-flex flex-column h-100 bg-light">
              {activeChat ? (
                <>
                  {/* Chat Header */}
                  <div className="chat-header p-3 bg-white border-bottom d-flex align-items-center justify-content-between shadow-sm z-1">
                    <div className="d-flex align-items-center">
                      {isMobileView && (
                        <Button variant="link" className="text-dark p-0 me-3" onClick={() => setActiveChat(null)}>
                          <FaArrowLeft size={20} />
                        </Button>
                      )}
                      <div className="chat-avatar me-3 bg-primary text-white d-flex align-items-center justify-content-center rounded-circle" style={{ width: '40px', height: '40px' }}>
                        {activeChat.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h6 className="mb-0">{activeChat.name}</h6>
                        <small className="text-success">
                          {activeChat.type === 'student' ? 'Student' : (activeChat.type === 'batch' ? 'Batch Broadcast' : 'Course Broadcast')}
                        </small>
                      </div>
                    </div>
                    
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="link" className="text-dark p-0 shadow-none hide-caret">
                        <FaEllipsisV />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setShowClearConfirm(true)} className="text-danger">
                          <FaTrash className="me-2" /> Clear Chat
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  {/* Chat Messages */}
                  <div className="chat-messages flex-grow-1 p-3 overflow-auto" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")' }}>
                    {messages.length === 0 ? (
                      <div className="d-flex h-100 align-items-center justify-content-center flex-column text-muted">
                        <div className="p-4 bg-white rounded-circle shadow-sm mb-3">
                          <FaPaperPlane size={40} className="text-primary opacity-50" />
                        </div>
                        <p>No messages yet. Start the conversation!</p>
                        {activeChat.type !== 'student' && (
                          <small className="text-center w-75">
                            Messages sent here will be broadcasted to all students in this {activeChat.type}. They cannot reply directly to this thread.
                          </small>
                        )}
                      </div>
                    ) : (
                      messages.map((msg, index) => {
                        const isAdmin = msg.sender_type === 'admin';
                        return (
                          <div key={msg.id || index} className={`message-wrapper d-flex mb-3 ${isAdmin ? 'justify-content-end' : 'justify-content-start'}`}>
                            {!isAdmin && (
                              <div className="d-flex align-items-center me-2 opacity-75">
                                <Button variant="link" className="text-danger p-0 shadow-none" onClick={() => handleDeleteMessage(msg.id)} title="Delete message">
                                  <FaTrash size={12} />
                                </Button>
                              </div>
                            )}
                            <div className={`message-bubble p-3 shadow-sm ${isAdmin ? 'bg-primary text-white rounded-start rounded-bottom' : 'bg-white text-dark rounded-end rounded-bottom'}`} style={{ maxWidth: '75%' }}>
                              {msg.attachment_url && renderAttachment(msg.attachment_url)}
                              {msg.message && <div className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>}
                              <div className={`message-time text-end mt-1 ${isAdmin ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                                {formatTime(msg.created_at)}
                              </div>
                            </div>
                            {isAdmin && (
                              <div className="d-flex align-items-center ms-2 opacity-75">
                                <Button variant="link" className="text-danger p-0 shadow-none" onClick={() => handleDeleteMessage(msg.id)} title="Delete message">
                                  <FaTrash size={12} />
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Chat Input */}
                  <div className="chat-input-area p-3 bg-white border-top">
                    {attachment && (
                      <div className="mb-2 p-2 bg-light rounded d-flex align-items-center justify-content-between border">
                        <div className="text-truncate me-3">
                          <FaPaperclip className="me-2" /> {attachment.name}
                        </div>
                        <Button variant="link" className="text-danger p-0" onClick={() => { setAttachment(null); fileInputRef.current.value = ''; }}>
                          <FaTimes />
                        </Button>
                      </div>
                    )}
                    <Form onSubmit={handleSendMessage} className="d-flex align-items-end">
                      <Button 
                        variant="light" 
                        className="rounded-circle text-muted p-2 me-2 shadow-none" 
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaPaperclip size={20} />
                      </Button>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        className="d-none" 
                        onChange={(e) => setAttachment(e.target.files[0])} 
                      />
                      <Form.Control
                        as="textarea"
                        ref={textareaRef}
                        rows={1}
                        placeholder="Type a message..."
                        className="border-0 shadow-none bg-light rounded-4 px-4 py-2 resize-none"
                        value={messageInput}
                        onChange={(e) => {
                          setMessageInput(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                        style={{ resize: 'none', maxHeight: '120px', overflowY: 'auto' }}
                      />
                      <Button 
                        type="submit" 
                        variant="primary" 
                        className="rounded-circle ms-2 d-flex align-items-center justify-content-center shadow-sm"
                        style={{ width: '45px', height: '45px', flexShrink: 0 }}
                        disabled={!messageInput.trim() && !attachment}
                      >
                        <FaPaperPlane />
                      </Button>
                    </Form>
                  </div>
                </>
              ) : (
                <div className="d-flex h-100 align-items-center justify-content-center bg-light flex-column text-muted">
                  <div className="whatsapp-placeholder-img mb-4 opacity-50">
                    <FaUserCircle size={100} />
                  </div>
                  <h4>Sensible Academy Chat</h4>
                  <p>Select a student, batch, or course to start messaging.</p>
                </div>
              )}
            </Col>
          )}
        </Row>

        {/* Clear Chat Modal */}
        <Modal show={showClearConfirm} onHide={() => setShowClearConfirm(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Clear Chat</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to clear all messages with <strong>{activeChat?.name}</strong>? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleClearChat}>Clear Chat</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default AdminChat;
